import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'

const NOT_FOUND_PATH = '/404'

export type NavigationContextType = {
  basePath: string
  fullPath: string
  isNotFound: boolean
  navigate: (path: string, withReload?: boolean) => void
  pageNotFound: () => void
}

const NavigationContext = React.createContext<NavigationContextType | undefined>(undefined)

export { NavigationContext }

export function useNavigation(): NavigationContextType {
  const context = React.useContext(NavigationContext)

  if (context === undefined) {
    throw new Error('useNavigation() is only available within an NavigationContext.Provider')
  }

  return context
}

export default function NavigationProvider(props: React.PropsWithChildren<{}>): ReactElement {
  const { push } = useHistory()
  const { pathname } = useLocation()
  const [state, setState] = useState({
    basePath: '/',
    fullPath: '/',
    isNotFound: false,
  })

  const navigate = useCallback(
    (path: string, withReload: boolean = false) => {
      if (withReload) {
        window.location.href = `${window.location.origin + path}`
      } else {
        push(path.startsWith('/') ? path : `/${path}`)
      }
    },
    [push]
  )

  const pageNotFound = useCallback(() => {
    navigate(NOT_FOUND_PATH, true)
  }, [navigate])

  useEffect(() => {
    const nextSeparator = pathname.indexOf('/', 1)
    const basePath = nextSeparator > 0 ? pathname.substr(0, nextSeparator) : pathname

    setState((prev) => ({ ...prev, basePath, fullPath: pathname, isNotFound: pathname === NOT_FOUND_PATH }))
  }, [pathname])

  const ctxValue = useMemo((): NavigationContextType => {
    return {
      ...state,
      navigate,
      pageNotFound,
    }
  }, [navigate, pageNotFound, state])

  return <NavigationContext.Provider value={ctxValue} {...props} />
}
